﻿import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject } from 'rxjs';
import { SessionStorageService, SessionStorage } from 'ngx-webstorage';

import { ApplicationService, OutOfOfficeSetting } from 'services/application/application.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    @SessionStorage() private _User: UserInfo;

    UserSubject: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(null);

    get User() {
        return this._User;
    }

    set User(_ui: UserInfo) {
        this._User = _ui;
        this.UserSubject.next(_ui);
    }

    constructor(
        private appSvc: ApplicationService,
        private storage: SessionStorageService,
        private route: ActivatedRoute,
        private router: Router,
        private sessionService: SessionStorageService
    ) {
        this.UserSubject.next(this._User);
    }

    // #region Clear User Service Call

    ClearUser() {
        this.User = null;
        this.sessionService.clear('_User');

        // Clear out all session specific settings.
        this.storage.clear('LastSearchResults');
        this.storage.clear('LastSearchOptions');
        this.storage.clear('SuppressOutOfOfficeAlert');
        this.storage.clear('_AllSubordinatesSupervisorSAPID');
        this.storage.clear('_AllSubordinates');

        this.appSvc.CurrentRequest = null;
    }

    // #endregion

    // #region Set Designee Edit Rights Service Call

    SetDesigneeEditRights(rights: DesigneeEditRights) {
        const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

        this.User = newUser;
    }

    // #endregion

    // #region Set User Service Call

    SetUser(user: UserInfo) {
        // const currole = this.User && this.User.CurrentRole;
        // user.CurrentRole = currole;

        this.User = user;
    }

    // #endregion

    // #region Set User Current Role Service Call

    SetUserCurrentRole(role: string) {
        if (this.User.CurrentRole != null && role === this.User.CurrentRole) {
            return;
        }

        // To trigger proper session storage, we need to create a new object reference, rather than just updating our existing User object.
        const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

        // Update the current role field.
        newUser.CurrentRole = role;

        // If the current role will now be 'Cabinet', look to see if the user has an active Out of Office setting for that role.
        if (role === 'Cabinet') {
            const cabRole = newUser.Roles.find(r => r.RoleName === 'Cabinet');

            newUser.OutOfOfficeActive = cabRole && cabRole.OutOfOfficeActive;
        } else {
            // Other roles cannot have Out of Office settings, so shut down the flag.
            newUser.OutOfOfficeActive = false;
        }

        // Now, persist it to session storage and then broadcast it for listeners.
        this.User = newUser;

        // Go to the home screen for this role.
        const homeroute = `/home`;
        this.router.navigate([homeroute]);
    }

    // #endregion

    // #region Update AuthToken Service Call

    UpdateAuthToken(AuthToken: any) {
        // If the authtoken has changed, update the user record.
        if (AuthToken !== this.User.AuthToken) {
            // To trigger proper session storage, we need to create a new object reference, rather than just updating our
            // existing User object.
            const newUser: UserInfo = <UserInfo>JSON.parse(JSON.stringify(this.User));

            // Update the AuthToken field
            newUser.AuthToken = AuthToken;

            // Now, persist it to session storage and then broadcast it for listeners
            this.User = newUser;
        }
    }

    // #endregion
}

// #region Custom Classes

// #region Designee Edit Rights Class

export class DesigneeEditRights {
    CanEditSchool: boolean;
    CanEditTeacher: boolean;
    CanUploadVideos: boolean;
}

// #endregion

// #region User Info Class

export class UserInfo {
    SAPID: string;
    UserID: number;
    FirstName: string;
    LastName: string;
    AccountName: string;
    AuthToken: string;
    Roles: UserRole[];
    RoleInfo: UserRoleInfo;
    IsImpersonation: boolean;
    ImpersonatorSAPID: string;
    ImpersonatorUserID?: number;
    ImpersonatorUsername?: string;
    CurrentRole: string;
    ImpersonationAvailable: boolean;
    OutOfOfficeActive: boolean;

    TrainingMode?: boolean;
}

// #endregion

// #region User Role Class

export class UserRole {
    RoleName: string;
    SortOrder: number;
    OutOfOfficeActive: boolean;
    OutOfOfficeSettings: OutOfOfficeSetting[];

    RoleNumber?: number;
}

// #endregion

// #region User Role Info Class

export class UserRoleInfo {
    IsDeveloper: boolean;
    IsDeputySuperintendent: boolean;
    IsSuperintendent: boolean;
    IsCabinet: boolean;
    IsBoardMember: boolean;
    IsBoardAssistant: boolean;
    IsFacilitator: boolean;
    IsResponder: boolean;
    IsMultiRole: boolean;
}

// #endregion

// #endregion
